import {
  Account,
  useTestAccountPhone,
  getListAccountsQueryKey,
} from "@workspace/api-client-react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type RowSelectionState,
  type OnChangeFn,
  type VisibilityState,
} from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PhoneStatusIcon } from "@/components/phone-status-icon";
import { BankNumbersPopover } from "@/components/bank-numbers";
import { Loader2, Send, SlidersHorizontal } from "lucide-react";
import { Link } from "wouter";
import { toast } from "sonner";
import { usePersistentState } from "@/hooks/use-persistent-state";

const columnHelper = createColumnHelper<Account>();

function isHealthy(account: Account) {
  return account.phoneStatus === "good";
}

export type HealthState = "good" | "warning" | "bad";

export function accountHealthState(account: Account): HealthState {
  if (account.phoneStatus === "good") return "good";
  if (account.phoneStatus === "bad") return "bad";
  return "warning";
}

const HEALTH_RING: Record<HealthState, string> = {
  good: "bg-green-500 ring-green-500/30",
  warning: "bg-amber-500 ring-amber-500/30",
  bad: "bg-red-500 ring-red-500/30",
};

const HEALTH_LABEL: Record<HealthState, string> = {
  good: "Good",
  warning: "Warning",
  bad: "Bad",
};

function HealthRing({ account }: { account: Account }) {
  const state = accountHealthState(account);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center justify-center">
          <span
            className={`h-2 w-2 rounded-full ring-2 ${HEALTH_RING[state]}`}
          />
        </span>
      </TooltipTrigger>
      <TooltipContent>{HEALTH_LABEL[state]}</TooltipContent>
    </Tooltip>
  );
}

function TestPhoneButton({ account }: { account: Account }) {
  const qc = useQueryClient();
  const healthy = isHealthy(account);
  const test = useTestAccountPhone({
    mutation: {
      onSuccess: (res) => {
        if (res.status === "sent") toast.success(`Test SMS sent to ${res.phoneNumber}`);
        else if (res.status === "skipped") toast.warning(res.error || "No phone on file");
        else toast.error(res.error || "Test SMS failed");
        qc.invalidateQueries({ queryKey: getListAccountsQueryKey() });
      },
      onError: () => toast.error("Twilio is not configured"),
    },
  });
  const button = (
    <Button
      size="sm"
      variant="outline"
      className="h-6 px-2 text-[10px]"
      disabled={test.isPending || !account.wrPhone || healthy}
      onClick={() => test.mutate({ id: account.id })}
    >
      {test.isPending ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <Send className="h-3 w-3" />
      )}
      <span className="ml-1">Test</span>
    </Button>
  );
  if (!healthy) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0}>{button}</span>
      </TooltipTrigger>
      <TooltipContent>Phone is healthy — no test needed</TooltipContent>
    </Tooltip>
  );
}

function BankCell({ account }: { account: Account }) {
  if (!account.bankMappingStatus) {
    return (
      <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30 font-mono text-[9px] uppercase">
        No Bank
      </Badge>
    );
  }
  const label = account.bankCardName || account.bankName || account.bankSource || "Bank";
  return (
    <BankNumbersPopover
      details={{
        label,
        variant: account.bankVariant,
        routingNumber: account.bankRouting,
        accountNumber: account.bankAccount,
        routingLast4: account.bankRoutingLast4,
        accountLast4: account.bankAccountLast4,
        variantId: account.bankVariantId,
        mappingStatus: account.bankMappingStatus,
        accountEmail: account.vendorAccountEmail,
      }}
    />
  );
}

const columns = [
  columnHelper.display({
    id: "health-ring",
    header: "",
    enableHiding: false,
    cell: (info) => <HealthRing account={info.row.original} />,
  }),
  columnHelper.display({
    id: "select",
    enableHiding: false,
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllRowsSelected()
            ? true
            : table.getIsSomeRowsSelected()
              ? "indeterminate"
              : false
        }
        onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
        aria-label="Select all on page"
        className="opacity-0 group-hover/hdr:opacity-100 data-[state=checked]:opacity-100 data-[state=indeterminate]:opacity-100 transition-opacity"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className="opacity-0 group-hover:opacity-100 data-[state=checked]:opacity-100 data-[state=indeterminate]:opacity-100 transition-opacity"
      />
    ),
  }),
  columnHelper.accessor("vendorAccountEmail", {
    header: "Email",
    enableHiding: false,
    cell: (info) => (
      <Link href={`/accounts/${info.row.original.id}`} className="text-primary hover:underline font-medium text-xs">
        {info.getValue()}
      </Link>
    ),
  }),
  columnHelper.accessor("accountName", {
    header: "Name",
    enableHiding: false,
    cell: (info) => <span className="text-xs text-secondary-foreground">{info.getValue() || "-"}</span>,
  }),
  columnHelper.accessor("pullerName", {
    header: "Puller",
    enableHiding: false,
    cell: (info) => <span className="text-xs">{info.getValue() || "-"}</span>,
  }),
  columnHelper.accessor("wrPhone", {
    header: "Phone",
    enableHiding: false,
    cell: (info) => <span className="font-mono text-xs">{info.getValue() || "-"}</span>,
  }),
  columnHelper.accessor("phoneStatus", {
    header: "Health",
    enableHiding: false,
    cell: (info) => {
      const a = info.row.original;
      return (
        <PhoneStatusIcon
          status={a.phoneStatus}
          lastSmsTime={a.lastSmsTime}
          phoneType={a.wrPhoneType}
          phone={a.wrPhone}
          manual={a.phoneStatusManual}
        />
      );
    },
  }),
  columnHelper.display({
    id: "test",
    header: "",
    enableHiding: false,
    cell: (info) => <TestPhoneButton account={info.row.original} />,
  }),
  columnHelper.accessor("activeInventoryCount", {
    header: "Inv Count",
    enableHiding: false,
    cell: (info) => <span className="font-mono text-xs">{info.getValue() || 0}</span>,
  }),
  columnHelper.display({
    id: "bank",
    header: "Bank",
    enableHiding: false,
    cell: (info) => <BankCell account={info.row.original} />,
  }),
  columnHelper.accessor("ampMatch", {
    header: "AMP",
    enableHiding: false,
    cell: (info) => info.getValue() ? (
      <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 font-mono text-[9px] uppercase">
        Match
      </Badge>
    ) : "-",
  }),
  columnHelper.accessor("tmPassword", {
    id: "tmPassword",
    header: "TM Password",
    enableHiding: true,
    cell: (info) => {
      const val = info.getValue();
      return val ? (
        <span className="font-mono text-xs">{val}</span>
      ) : (
        <span className="text-muted-foreground/50 text-xs">—</span>
      );
    },
  }),
];

// The set of columns that can be toggled on/off.
const HIDEABLE_COLUMNS: { id: string; label: string }[] = [
  { id: "tmPassword", label: "TM Password" },
];

interface AccountsTableProps {
  data: Account[];
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
}

export function AccountsTable({ data, rowSelection, onRowSelectionChange }: AccountsTableProps) {
  const [columnVisibility, setColumnVisibility] = usePersistentState<VisibilityState>(
    "tex-accounts-col-visibility",
    { tmPassword: false },
  );

  const table = useReactTable({
    data,
    columns,
    state: { rowSelection, columnVisibility },
    onRowSelectionChange,
    onColumnVisibilityChange: setColumnVisibility,
    getRowId: (row) => String(row.id),
    enableRowSelection: (row) => !isHealthy(row.original),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="w-full">
      <div className="flex items-center justify-end px-3 py-1.5 border-b border-border/50">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs gap-1.5 rounded-full">
              <SlidersHorizontal className="h-3 w-3" />
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
              Toggle columns
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {HIDEABLE_COLUMNS.map(({ id, label }) => {
              const col = table.getColumn(id);
              if (!col) return null;
              return (
                <DropdownMenuCheckboxItem
                  key={id}
                  checked={col.getIsVisible()}
                  onCheckedChange={(value) => col.toggleVisibility(value)}
                  className="text-xs"
                >
                  {label}
                </DropdownMenuCheckboxItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-panel z-10 border-b border-border shadow-sm">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="group/hdr">
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-muted-foreground font-mono font-medium whitespace-nowrap">
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => {
              return (
                <tr
                  key={row.id}
                  className="border-b border-border/50 hover:bg-muted/50 transition-colors group cursor-default"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="py-1.5 px-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={columns.length} className="text-center py-8 text-muted-foreground text-sm">
                No accounts found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
